import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Sparkline, SparklineText } from "@/components/ui/sparkline";
import { Plan } from "@/types/supabase";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { UsageTrackingService } from "@/services/usageTrackingService";
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Calendar,
  Zap,
} from "lucide-react";

interface UsageTrackingTableProps {
  currentPlan: Plan;
  organization: any;
}

interface UsageMetric {
  key: string;
  label: string;
  current: number;
  limit: number | null;
  unit: string;
  shortUnit: string;
  trend: number[];
  resetDate?: Date;
}

export const UsageTrackingTable = ({
  currentPlan,
  organization,
}: UsageTrackingTableProps) => {
  const [metrics, setMetrics] = useState<UsageMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const { toast } = useToast();

  // Generate mock 7-day trend data for now (will be replaced with real data)
  const generateMockTrend = (current: number, limit: number | null) => {
    const days = 7;
    const trend = [];
    const baseValue = current * 0.7; // Start at 70% of current

    for (let i = 0; i < days; i++) {
      const variation = (Math.random() - 0.5) * 0.3; // ±15% variation
      const dayValue = Math.max(
        0,
        baseValue +
          baseValue * variation +
          (i * (current - baseValue)) / (days - 1),
      );
      trend.push(Math.round(dayValue));
    }
    return trend;
  };

  useEffect(() => {
    const loadUsageMetrics = async () => {
      try {
        setLoading(true);

        // Calculate next reset date (first day of next month)
        const nextReset = new Date();
        nextReset.setMonth(nextReset.getMonth() + 1);
        nextReset.setDate(1);
        nextReset.setHours(0, 0, 0, 0);

        let usageMetrics: UsageMetric[] = [
          {
            key: "conversations",
            label: "Monthly Conversations",
            current: organization.conversations_this_month || 0,
            limit: currentPlan.features.max_conversations_per_month,
            unit: " conversations",
            shortUnit: " convos",
            trend: generateMockTrend(
              organization.conversations_this_month || 0,
              currentPlan.features.max_conversations_per_month,
            ),
            resetDate: nextReset,
          },
          {
            key: "ai_generations",
            label: "AI Generations",
            current: organization.ai_generations_this_month || 0,
            limit: currentPlan.features.max_ai_generations_per_month,
            unit: " generations",
            shortUnit: " gens",
            trend: generateMockTrend(
              organization.ai_generations_this_month || 0,
              currentPlan.features.max_ai_generations_per_month,
            ),
            resetDate: nextReset,
          },
          {
            key: "voice_minutes",
            label: "Voice Minutes",
            current: organization.voice_minutes_used_this_month || 0,
            limit: currentPlan.features.max_voice_minutes_per_month,
            unit: " minutes",
            shortUnit: " min",
            trend: generateMockTrend(
              organization.voice_minutes_used_this_month || 0,
              currentPlan.features.max_voice_minutes_per_month,
            ),
            resetDate: nextReset,
          },
          {
            key: "voice_calls",
            label: "Voice Calls",
            current: organization.voice_calls_this_month || 0,
            limit: currentPlan.features.max_voice_calls_per_month,
            unit: " calls",
            shortUnit: " calls",
            trend: generateMockTrend(
              organization.voice_calls_this_month || 0,
              currentPlan.features.max_voice_calls_per_month,
            ),
            resetDate: nextReset,
          },
          {
            key: "api_calls",
            label: "API Calls",
            current: organization.api_calls_this_month || 0,
            limit: currentPlan.features.max_api_calls_per_month,
            unit: " calls",
            shortUnit: " calls",
            trend: generateMockTrend(
              organization.api_calls_this_month || 0,
              currentPlan.features.max_api_calls_per_month,
            ),
            resetDate: nextReset,
          },
          {
            key: "active_agents",
            label: "Active Agents",
            current: organization.active_agents_count || 0,
            limit: currentPlan.features.max_active_agents,
            unit: " agents",
            shortUnit: " agents",
            trend: generateMockTrend(
              organization.active_agents_count || 0,
              currentPlan.features.max_active_agents,
            ),
          },
          {
            key: "team_members",
            label: "Team Members",
            current: organization.team_members_count || 0,
            limit: currentPlan.features.max_team_members,
            unit: " members",
            shortUnit: " members",
            trend: generateMockTrend(
              organization.team_members_count || 0,
              currentPlan.features.max_team_members,
            ),
          },
          {
            key: "storage",
            label: "Storage Usage",
            current: organization.storage_usage_mb || 0,
            limit: currentPlan.features.max_storage_mb,
            unit: formatStorage(currentPlan.features.max_storage_mb || 0).unit,
            shortUnit: formatStorage(currentPlan.features.max_storage_mb || 0)
              .shortUnit,
            trend: generateMockTrend(
              organization.storage_usage_mb || 0,
              currentPlan.features.max_storage_mb,
            ),
          },
        ];

        // When chat agents are hidden, drop chat-related metrics (conversations only)
        if (organization?.hide_chat_agents) {
          usageMetrics = usageMetrics.filter((m) => m.key !== "conversations");
        }

        // Filter out metrics that are not applicable (have 0 limit and 0 current usage)
        const activeMetrics = usageMetrics.filter(
          (metric) =>
            metric.limit !== 0 && (metric.limit !== null || metric.current > 0),
        );

        setMetrics(activeMetrics);
        setLastUpdate(new Date());
      } catch (error) {
        console.error("Error loading usage metrics:", error);
        toast({
          variant: "destructive",
          title: "Error loading usage data",
          description: "Please refresh the page to try again.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadUsageMetrics();
  }, [currentPlan, organization, toast]);

  const getUsagePercentage = (current: number, limit: number | null) => {
    if (limit === null) return 0; // unlimited
    if (limit <= 0) return 100; // feature disabled
    return Math.min(Math.round((current / limit) * 100), 100);
  };

  const formatLimit = (limit: number | null, unit: string) => {
    if (limit === null) return "Unlimited";
    if (limit === 0) return "Not available";
    return `${limit.toLocaleString()}${unit}`;
  };

  const formatStorage = (mb: number) => {
    if (mb >= 1000) {
      return {
        value: (mb / 1000).toFixed(1),
        unit: " GB",
        shortUnit: " GB",
      };
    }
    return {
      value: mb.toString(),
      unit: " MB",
      shortUnit: " MB",
    };
  };

  const getUsageStatus = (percentage: number) => {
    if (percentage >= 100)
      return {
        color: "red",
        text: "Over limit",
        variant: "destructive" as const,
      };
    if (percentage >= 90)
      return {
        color: "orange",
        text: "Near limit",
        variant: "secondary" as const,
      };
    if (percentage >= 75)
      return {
        color: "yellow",
        text: "High usage",
        variant: "outline" as const,
      };
    return { color: "green", text: "Normal", variant: "outline" as const };
  };

  const getTrendDirection = (trend: number[]) => {
    if (trend.length < 2) return "stable";
    const recent = trend.slice(-3).reduce((a, b) => a + b, 0) / 3;
    const earlier = trend.slice(0, 3).reduce((a, b) => a + b, 0) / 3;
    const change = (recent - earlier) / earlier;

    if (change > 0.1) return "up";
    if (change < -0.1) return "down";
    return "stable";
  };

  const handleManageSubscription = async () => {
    if (!organization?.id) return;

    try {
      const { data, error } = await supabase.functions.invoke(
        "create-billing-portal",
        {
          body: {
            organizationId: organization.id,
            returnUrl: window.location.href,
          },
        },
      );

      if (error)
        throw new Error(
          error.message || "Failed to create billing portal session",
        );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No billing portal URL returned");
      }
    } catch (error) {
      console.error("Error opening billing portal:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to open billing portal. Please try again.",
      });
    }
  };

  const handleUpgrade = () => {
    // Navigate to the subscription plans tab
    const url = new URL(window.location.href);
    url.searchParams.set("tab", "subscription");
    window.location.href = url.toString();
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Usage Overview</CardTitle>
              <CardDescription>Loading your usage data...</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-12 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const overLimitMetrics = metrics.filter(
    (m) => getUsagePercentage(m.current, m.limit) >= 100,
  );
  const nearLimitMetrics = metrics.filter((m) => {
    const pct = getUsagePercentage(m.current, m.limit);
    return pct >= 90 && pct < 100;
  });

  return (
    <div className="space-y-6">
      {/* Alerts for over-limit or near-limit usage */}
      {overLimitMetrics.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Usage limits exceeded:</strong>{" "}
            {overLimitMetrics.map((m) => m.label).join(", ")}.
            <Button
              variant="link"
              className="h-auto p-0 ml-2 text-red-600"
              onClick={handleUpgrade}
            >
              Upgrade your plan
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {nearLimitMetrics.length > 0 && overLimitMetrics.length === 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Approaching limits:</strong>{" "}
            {nearLimitMetrics.map((m) => m.label).join(", ")}.
            <Button
              variant="link"
              className="h-auto p-0 ml-2"
              onClick={handleUpgrade}
            >
              Consider upgrading
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Usage Overview - {currentPlan.name}
              </CardTitle>
              <CardDescription>
                Real-time usage metrics with 7-day trends
                <span className="text-xs text-gray-500 ml-2">
                  Last updated: {lastUpdate.toLocaleTimeString()}
                </span>
              </CardDescription>
            </div>
            <Button onClick={handleManageSubscription} variant="outline">
              Manage Subscription
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Metric</TableHead>
                  <TableHead className="text-right">Used</TableHead>
                  <TableHead className="text-right">Limit</TableHead>
                  <TableHead className="text-center w-[80px]">%</TableHead>
                  <TableHead className="text-center w-[100px]">
                    Trend (7d)
                  </TableHead>
                  <TableHead className="text-center w-[100px]">Reset</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {metrics.map((metric) => {
                  const percentage = getUsagePercentage(
                    metric.current,
                    metric.limit,
                  );
                  const status = getUsageStatus(percentage);
                  const trendDirection = getTrendDirection(metric.trend);

                  return (
                    <TableRow key={metric.key} className="hover:bg-gray-50">
                      <TableCell className="font-medium">
                        <div>
                          <div>{metric.label}</div>
                          <div className="text-xs text-gray-500 hidden sm:block">
                            {metric.key === "storage"
                              ? `${formatStorage(metric.current).value}${formatStorage(metric.current).shortUnit}`
                              : `${metric.current.toLocaleString()}${metric.shortUnit}`}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {metric.key === "storage"
                          ? formatStorage(metric.current).value
                          : metric.current.toLocaleString()}
                        <span className="text-xs text-gray-500 ml-1 hidden sm:inline">
                          {metric.key === "storage"
                            ? formatStorage(metric.current).shortUnit
                            : metric.shortUnit}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {metric.key === "storage" && metric.limit
                          ? `${formatStorage(metric.limit).value}${formatStorage(metric.limit).shortUnit}`
                          : formatLimit(metric.limit, metric.shortUnit)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <Badge
                            variant={status.variant}
                            className="text-xs px-2 py-0"
                          >
                            {percentage >= 100
                              ? `+${percentage - 100}%`
                              : `${percentage}%`}
                          </Badge>
                          {percentage >= 100 && (
                            <span className="text-xs text-red-600 font-medium">
                              Over
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <div className="hidden sm:block">
                            <Sparkline
                              data={metric.trend}
                              width={60}
                              height={20}
                              color={
                                trendDirection === "up"
                                  ? "#ef4444"
                                  : trendDirection === "down"
                                    ? "#22c55e"
                                    : "#6b7280"
                              }
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-xs font-mono hidden sm:inline">
                              <SparklineText data={metric.trend} />
                            </span>
                            {trendDirection === "up" && (
                              <TrendingUp className="h-3 w-3 text-red-500" />
                            )}
                            {trendDirection === "down" && (
                              <TrendingDown className="h-3 w-3 text-green-500" />
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {metric.resetDate ? (
                          <div className="flex flex-col items-center">
                            <Calendar className="h-3 w-3 text-gray-400 mb-1" />
                            <span className="text-xs text-gray-500">
                              {metric.resetDate.toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                              })}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Mobile-friendly cards view for smaller screens */}
          <div className="sm:hidden space-y-4 mt-6">
            {metrics.map((metric) => {
              const percentage = getUsagePercentage(
                metric.current,
                metric.limit,
              );
              const status = getUsageStatus(percentage);
              const trendDirection = getTrendDirection(metric.trend);

              return (
                <Card key={`mobile-${metric.key}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-sm">{metric.label}</h4>
                      <Badge variant={status.variant} className="text-xs">
                        {percentage >= 100
                          ? `+${percentage - 100}% over`
                          : `${percentage}%`}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-mono">
                        {metric.key === "storage"
                          ? `${formatStorage(metric.current).value}${formatStorage(metric.current).shortUnit} / ${metric.limit ? `${formatStorage(metric.limit).value}${formatStorage(metric.limit).shortUnit}` : "Unlimited"}`
                          : `${metric.current.toLocaleString()} / ${formatLimit(metric.limit, metric.unit)}`}
                      </span>
                      <div className="flex items-center gap-2">
                        <SparklineText data={metric.trend} />
                        {trendDirection === "up" && (
                          <TrendingUp className="h-3 w-3 text-red-500" />
                        )}
                        {trendDirection === "down" && (
                          <TrendingDown className="h-3 w-3 text-green-500" />
                        )}
                      </div>
                    </div>
                    {metric.resetDate && (
                      <div className="text-xs text-gray-500 mt-1">
                        Resets {metric.resetDate.toLocaleDateString()}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
